/*
 * Helpers for the dashboard history charts (src/components/common/HistoricLineChart.js).
 *
 * The backend returns each history as a {"<epoch_ms>": "<value>"} map with one sample every
 * ~30 minutes, so a few months are thousands of points: more than the chart has pixels.
 * Above MAX_POINTS the samples are grouped into fixed time buckets:
 *   - numeric series: average per bucket, plus min/max (drawn as a band so short dips stay visible)
 *   - state series (on/off): share of samples "on" per bucket (0..1)
 * Long periods without samples become gaps (null points) instead of straight lines.
 */
import { addDays, addHours, addMonths, format, startOfDay, startOfHour, startOfMonth } from "date-fns";

export const MAX_POINTS = 300;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Bucket sizes, smallest first. The smallest is the sampling interval of the history cron.
const BUCKET_STEPS = [
  30 * MINUTE, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 7 * DAY,
];

/* "13.07 V" / "7.7 °C" / "4 W" -> number, or null when it is not a number */
export function parseNumber(text) {
  const n = parseFloat(String(text ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/*
 * {"<epoch_ms>": value} -> [{ ts, value }] sorted by time.
 * parse(value) returns a number, or null to skip the sample (e.g. offline placeholders).
 */
export function toSamples(map, parse = parseNumber) {
  if (!map || typeof map !== "object") return [];
  const samples = [];
  for (const [key, raw] of Object.entries(map)) {
    const ts = Number(key);
    const value = parse(raw);
    if (Number.isFinite(ts) && value !== null) samples.push({ ts, value });
  }
  return samples.sort((a, b) => a.ts - b.ts);
}

export const parseState = (text) => (text === "on" ? 1 : text === "off" ? 0 : null);

/* Smallest bucket that keeps the span within maxPoints buckets */
export function pickBucketMs(spanMs, maxPoints = MAX_POINTS) {
  const target = spanMs / maxPoints;
  return BUCKET_STEPS.find((step) => step >= target) ?? BUCKET_STEPS[BUCKET_STEPS.length - 1];
}

/* "30 min", "6 h", "1 day", "7 days" */
export function formatDuration(ms) {
  if (ms < HOUR) return `${Math.round(ms / MINUTE)} min`;
  if (ms < DAY) return `${Math.round(ms / HOUR)} h`;
  const days = Math.round(ms / DAY);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/*
 * Builds the chart points.
 *   samples: [{ ts, value }] sorted by time
 *   kind: "numeric" (average + min/max) or "state" (share of time on)
 * Returns { points, reduced, bucketMs, sampleCount }.
 *   points: [{ ts, value, range: [min, max], count }] with { ts, value: null, range: null } gap markers
 *   reduced: true when buckets were used (more samples than MAX_POINTS)
 */
export function buildSeries(samples, { kind = "numeric", maxPoints = MAX_POINTS } = {}) {
  const sampleCount = samples.length;
  if (sampleCount === 0) return { points: [], reduced: false, bucketMs: null, sampleCount };

  const reduced = sampleCount > maxPoints;
  let points;
  let bucketMs = null;

  if (!reduced) {
    points = samples.map(({ ts, value }) => ({ ts, value, range: [value, value], count: 1 }));
  } else {
    const from = samples[0].ts;
    bucketMs = pickBucketMs(samples[sampleCount - 1].ts - from, maxPoints);
    const buckets = new Map();
    for (const { ts, value } of samples) {
      const index = Math.floor((ts - from) / bucketMs);
      const b = buckets.get(index);
      if (b) {
        b.sum += value;
        b.min = Math.min(b.min, value);
        b.max = Math.max(b.max, value);
        b.count += 1;
      } else {
        buckets.set(index, { sum: value, min: value, max: value, count: 1 });
      }
    }
    points = [...buckets.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([index, b]) => {
        const avg = b.sum / b.count;
        return {
          ts: from + index * bucketMs + bucketMs / 2, // bucket center
          value: kind === "state" ? avg : round(avg),
          range: [round(b.min), round(b.max)],
          count: b.count,
        };
      });
  }

  return { points: withGaps(points, bucketMs), reduced, bucketMs, sampleCount };
}

/*
 * Inserts a null point between two points that are too far apart, so the line breaks
 * instead of drawing a straight segment over a period without data (device offline, cron stopped).
 */
function withGaps(points, bucketMs) {
  if (points.length < 2) return points;
  const gaps = [];
  for (let i = 1; i < points.length; i++) gaps.push(points[i].ts - points[i - 1].ts);
  const typical = [...gaps].sort((a, b) => a - b)[Math.floor(gaps.length / 2)];
  const threshold = Math.max(3 * (bucketMs ?? typical), 2 * HOUR);

  const result = [points[0]];
  for (let i = 1; i < points.length; i++) {
    if (points[i].ts - points[i - 1].ts > threshold) {
      result.push({ ts: (points[i].ts + points[i - 1].ts) / 2, value: null, range: null, count: 0 });
    }
    result.push(points[i]);
  }
  return result;
}

const round = (n) => Math.round(n * 100) / 100;

/* Tick steps for the time axis, smallest first */
const TICK_STEPS = [
  { ms: HOUR, add: (d) => addHours(d, 1), start: startOfHour },
  { ms: 2 * HOUR, add: (d) => addHours(d, 2), start: startOfHour },
  { ms: 3 * HOUR, add: (d) => addHours(d, 3), start: startOfHour },
  { ms: 6 * HOUR, add: (d) => addHours(d, 6), start: startOfDay },
  { ms: 12 * HOUR, add: (d) => addHours(d, 12), start: startOfDay },
  { ms: DAY, add: (d) => addDays(d, 1), start: startOfDay },
  { ms: 2 * DAY, add: (d) => addDays(d, 2), start: startOfDay },
  { ms: 7 * DAY, add: (d) => addDays(d, 7), start: startOfDay },
  { ms: 14 * DAY, add: (d) => addDays(d, 14), start: startOfDay },
  { ms: 30 * DAY, add: (d) => addMonths(d, 1), start: startOfMonth },
  { ms: 61 * DAY, add: (d) => addMonths(d, 2), start: startOfMonth },
  { ms: 91 * DAY, add: (d) => addMonths(d, 3), start: startOfMonth },
];

/*
 * Ticks on calendar boundaries (hours, days, weeks, months) for [from, to], at most ~maxTicks.
 * Returns { ticks, formatTick } where formatTick(ts) gives the label for that granularity.
 */
export function timeTicks(from, to, maxTicks = 8) {
  const span = Math.max(to - from, HOUR);
  const step = TICK_STEPS.find((s) => span / s.ms <= maxTicks) ?? TICK_STEPS[TICK_STEPS.length - 1];

  const ticks = [];
  for (let d = step.start(new Date(from)); d.getTime() <= to; d = step.add(d)) {
    if (d.getTime() >= from) ticks.push(d.getTime());
  }

  let pattern;
  if (step.ms < DAY) pattern = span <= DAY ? "HH:mm" : "dd/MM HH:mm";
  else if (step.ms < 30 * DAY) pattern = "dd/MM";
  else pattern = "MMM yyyy";

  return { ticks, formatTick: (ts) => format(new Date(ts), pattern) };
}
